import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { ReportInlineModuleError } from '@americanexpress/travel-error-base';
import clsx from 'clsx';
import { useConditionalFeature } from '@americanexpress/conditional-features';
import { ProgressCircular } from '@americanexpress/dls-react';
import getIsWaitingForMultipleRewardsSelector from '../../selectors/getIsWaitingForMultipleRewardsSelector';
import { getCardType } from '../../utils/travelerDataUtils';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { MODULE_NAME } from '../../constants/modules';
import { useHomepageCardOffers } from '../../hooks/useHomepageCardOffers';
import { getCardOffersContentFromPersona } from '../../utils/personaUtils';
import CardOffersError from './CardOffersError';
import CardOffers from './CardOffers';
import { getAutomationCardOffersClassName } from '../../selectors/getHomeSelectors';
import { useTravelerData } from '../../hooks/useTravelerData';
import { usePersonaApi } from '../../hooks/usePersonaApi';
import { hasHomePageCardOffersRule, hasMultipleRewardsRule } from '../../constants/featureRulesConstants';
import style from './CardOffersContainer.scss';

const validateCardOffers = (cardOffers) => {
  if (!cardOffers || typeof cardOffers !== 'object') return false;

  const cardOfferEntries = Object.entries(cardOffers)
    .filter(([, value]) => value.length > 0);

  return cardOfferEntries.length > 0;
};

const CardOffersContainer = () => {
  const isInMrMarket = useConditionalFeature(hasMultipleRewardsRule);
  const isWaitingForMultipleRewards = useSelector(getIsWaitingForMultipleRewardsSelector);
  const hasHomePageCardOffers = useConditionalFeature(hasHomePageCardOffersRule);
  const automationCardOffersId = useSelector(
    getAutomationCardOffersClassName(hasHomePageCardOffers)
  );
  const { travelerData, travelerError } = useTravelerData();
  const travelerAccounts = travelerData?.loyalty?.accounts || [];
  const hasMultipleAccounts = travelerAccounts.length > 1;

  const {
    data: personaData,
    isLoading: personaLoading,
    error: personaError,
  } = usePersonaApi();

  const {
    error: homepageCardOffersError,
    data: homepageCardOffersData,
    isLoading: homepageCardOffersLoading,
  } = useHomepageCardOffers();

  const displayLoadingState = personaLoading
    || (!travelerError && isInMrMarket && hasMultipleAccounts && isWaitingForMultipleRewards)
    || homepageCardOffersLoading;

  if (displayLoadingState) {
    return (
      <div className={clsx('text-align-center pad flex', style.loaderContainer)}>
        <ProgressCircular size="lg" className="margin-auto" />
      </div>
    );
  }

  let cardOffersData;
  let hasCardOfferError;
  const cardType = getCardType(travelerData);

  if (hasHomePageCardOffers && homepageCardOffersData) {
    cardOffersData = homepageCardOffersData.body;
    hasCardOfferError = homepageCardOffersError;
  } else {
    cardOffersData = getCardOffersContentFromPersona(
      personaData
    );
    hasCardOfferError = personaError;
  }

  const hasInsufficientCardOffers = !validateCardOffers(cardOffersData);

  if (hasCardOfferError || hasInsufficientCardOffers) {
    return (
      <div className="pad-responsive-extra-tb" data-testid={`${MODULE_NAME}-card-offer-error`}>
        <section className="container">
          <ReportInlineModuleError
            moduleID={MODULE_NAME}
            otherData={{
              component: 'CardOffersContainer',
              causes: {
                hasCardOfferError: !!hasCardOfferError,
                hasInsufficientCardOffers,
              },
            }}
          >
            <ErrorMessage
              message={
                <FormattedMessage id="error.unableToLoadContentMessage" />
              }
            />
          </ReportInlineModuleError>
        </section>
      </div>
    );
  }

  return (
    <div
      data-testid={`${MODULE_NAME}-homepage-card-offers-container`}
      className="pad-3-b"
    >
      <section
        id={`${MODULE_NAME}-homepage-card-offers`}
        data-testid={automationCardOffersId}
        className={clsx(
          'container pad-0-lr-md-down',
          automationCardOffersId
        )}
      >
        <CardOffersError
          hasTravelerError={travelerError}
        />
        <CardOffers
          cardOffersData={cardOffersData}
          cardType={cardType}
          hasTravelerError={travelerError}
          travelerData={travelerData}
        />
      </section>
    </div>
  );
};

export default CardOffersContainer;
